import React, { useState, useRef, useCallback, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  IconButton,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  Divider,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import Chip from '@mui/material/Chip';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import DataTag from './DataTag';
import './TagEditor.css';

/**
 * TagEditor component - Visual editor for placing data tags on media
 * 
 * @param {Object} props
 * @param {string} props.mediaUrl - URL of the media to display
 * @param {string} props.mediaType - "image" or "video"
 * @param {Array} props.tags - Current array of tag configurations
 * @param {function} props.onTagsChange - Callback when tags are modified
 * @param {boolean} props.isSimulated - Whether data source is simulated (shows sim config fields). Deprecated: use groupDataSource instead.
 * @param {string} props.groupDataSource - Effective group-level data source ('live' | 'simulated'). Each tag can override via tag.dataSource.
 * @param {boolean} props.compact - When true, renders only the list + dialog without embedded image preview
 * @param {function} props.requireDataSource - Callback that returns false if data source not yet chosen (opens prompt)
 * @param {function} props.onReady - Called with { openAddDialog } so parent can trigger add programmatically
 */
const TagEditor = ({
  mediaUrl,
  mediaType = 'image',
  tags = [],
  onTagsChange,
  isSimulated: isSimulatedLegacy = false,
  groupDataSource = '',
  compact = false,
  requireDataSource,
  onReady,
}) => {
  // Resolve per-tag effective data source
  const getTagDataSource = useCallback((tag) => {
    return tag.dataSource || groupDataSource || (isSimulatedLegacy ? 'simulated' : 'live');
  }, [groupDataSource, isSimulatedLegacy]);

  const isTagSimulated = useCallback((tag) => {
    return getTagDataSource(tag) === 'simulated';
  }, [getTagDataSource]);
  const mediaContainerRef = useRef(null);
  const [selectedTagId, setSelectedTagId] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingTag, setEditingTag] = useState(null);
  const [draggedTagId, setDraggedTagId] = useState(null);
  const [statesText, setStatesText] = useState('');

  const generateId = () => `tag-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

  const openAddDialogRef = useRef(null);
  openAddDialogRef.current = () => {
    const newTag = {
      id: generateId(),
      label: 'New Tag',
      topicPath: '',
      position: { x: 50, y: 50 },
      style: {
        color: '#F28E2B',
        showLabel: true,
        backgroundColor: 'rgba(0, 38, 62, 0.95)',
      },
    };
    setEditingTag(newTag);
    setStatesText('');
    setEditDialogOpen(true);
  };

  useEffect(() => {
    onReady?.({ openAddDialog: () => openAddDialogRef.current?.() });
  }, [onReady]);

  // Calculate position as percentage from click event
  const getPositionFromEvent = useCallback((e) => {
    if (!mediaContainerRef.current) return { x: 50, y: 50 };
    
    const rect = mediaContainerRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    
    return {
      x: Math.max(5, Math.min(95, x)),
      y: Math.max(5, Math.min(95, y)),
    };
  }, []);

  // Handle click on media to add new tag
  const handleMediaClick = useCallback((e) => {
    if (e.target !== e.currentTarget && !e.target.classList.contains('tag-editor-media')) {
      return;
    }

    if (requireDataSource && !requireDataSource("tag")) return;

    const position = getPositionFromEvent(e);
    
    const newTag = {
      id: generateId(),
      label: 'New Tag',
      topicPath: '',
      position,
      style: {
        color: '#F28E2B',
        showLabel: true,
        backgroundColor: 'rgba(0, 38, 62, 0.95)',
      },
    };

    setEditingTag(newTag);
    setStatesText('');
    setEditDialogOpen(true);
  }, [getPositionFromEvent, requireDataSource]);

  // Handle tag drag for repositioning
  const handleTagDragStart = useCallback((tagId) => {
    setDraggedTagId(tagId);
  }, []);

  const handleTagDragEnd = useCallback((e, tagId) => {
    if (!draggedTagId || !mediaContainerRef.current) return;

    const position = getPositionFromEvent(e);
    
    const updatedTags = tags.map(tag => 
      tag.id === tagId ? { ...tag, position } : tag
    );
    
    onTagsChange(updatedTags);
    setDraggedTagId(null);
  }, [draggedTagId, tags, onTagsChange, getPositionFromEvent]);

  // Handle tag click for selection
  const handleTagClick = useCallback((e, tagId) => {
    e.stopPropagation();
    setSelectedTagId(tagId === selectedTagId ? null : tagId);
  }, [selectedTagId]);

  const handleEditTag = useCallback((tag) => {
    setEditingTag({ ...tag });
    setStatesText((tag.simulationConfig?.states || []).join(', '));
    setEditDialogOpen(true);
  }, []);

  // Delete a tag
  const handleDeleteTag = useCallback((tagId) => {
    const updatedTags = tags.filter(tag => tag.id !== tagId);
    onTagsChange(updatedTags);
    if (selectedTagId === tagId) {
      setSelectedTagId(null);
    }
  }, [tags, onTagsChange, selectedTagId]);

  const handleSaveTag = useCallback(() => {
    if (!editingTag) return;

    const tagToSave = { ...editingTag };
    if (isTagSimulated(tagToSave) && tagToSave.simulationConfig?.type === 'state') {
      tagToSave.simulationConfig = {
        ...tagToSave.simulationConfig,
        states: statesText.split(',').map(s => s.trim()).filter(Boolean),
      };
    }

    const existingIndex = tags.findIndex(t => t.id === tagToSave.id);

    let updatedTags;
    if (existingIndex >= 0) {
      updatedTags = tags.map(t => t.id === tagToSave.id ? tagToSave : t);
    } else {
      updatedTags = [...tags, tagToSave];
    }

    onTagsChange(updatedTags);
    setEditDialogOpen(false);
    setEditingTag(null);
  }, [editingTag, tags, onTagsChange, statesText, isTagSimulated]);

  // Update editing tag field
  const handleEditingTagChange = useCallback((field, value) => {
    setEditingTag(prev => ({
      ...prev,
      [field]: value,
    }));
  }, []);

  // Update editing tag style field
  const handleEditingTagStyleChange = useCallback((field, value) => {
    setEditingTag(prev => ({
      ...prev,
      style: {
        ...prev.style,
        [field]: value,
      },
    }));
  }, []);

  // Update editing tag simulationConfig field
  const handleSimConfigChange = useCallback((field, value) => {
    setEditingTag(prev => ({
      ...prev,
      simulationConfig: {
        ...prev.simulationConfig,
        [field]: value,
      },
    }));
  }, []);

  const addNewTag = useCallback(() => {
    if (requireDataSource && !requireDataSource("tag")) return;

    const newTag = {
      id: generateId(),
      label: 'New Tag',
      topicPath: '',
      position: { x: 50, y: 50 },
      style: {
        color: '#F28E2B',
        showLabel: true,
        backgroundColor: 'rgba(0, 38, 62, 0.95)',
      },
    };
    setEditingTag(newTag);
    setStatesText('');
    setEditDialogOpen(true);
  }, [requireDataSource]);

  const renderTagsList = () => (
    <List dense sx={{ maxHeight: 300, overflow: 'auto' }}>
      {tags.length === 0 ? (
        <ListItem>
          <ListItemText
            secondary={compact ? "No tags yet. Click 'Add Tag' to create one." : "No tags yet. Click on the image to add one."}
          />
        </ListItem>
      ) : (
        tags.map((tag) => {
          const nodeSimulated = isTagSimulated(tag);
          return (
          <ListItem
            key={tag.id}
            selected={selectedTagId === tag.id}
            onClick={() => setSelectedTagId(tag.id)}
            sx={{ cursor: 'pointer' }}
          >
            <Box
              component="label"
              sx={{
                width: 24,
                height: 24,
                borderRadius: '4px',
                backgroundColor: tag.style?.color || '#F28E2B',
                border: '1px solid #ccc',
                cursor: 'pointer',
                mr: 1,
                flexShrink: 0,
                position: 'relative',
                overflow: 'hidden',
              }}
            >
              <input
                type="color"
                value={tag.style?.color || '#F28E2B'}
                onChange={(e) => {
                  e.stopPropagation();
                  const updatedTags = tags.map(t =>
                    t.id === tag.id ? { ...t, style: { ...t.style, color: e.target.value } } : t
                  );
                  onTagsChange(updatedTags);
                }}
                onClick={(e) => e.stopPropagation()}
                style={{ opacity: 0, position: 'absolute', width: '100%', height: '100%', cursor: 'pointer' }}
              />
            </Box>
            <ListItemText
              primary={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {tag.label}
                  <Chip
                    label={nodeSimulated ? 'Sim' : 'Live'}
                    size="small"
                    color={nodeSimulated ? 'warning' : 'success'}
                    variant="outlined"
                    sx={{ height: 20, fontSize: '0.7rem' }}
                  />
                </Box>
              }
              secondary={nodeSimulated ? (tag.simulationConfig?.type || 'Simulated') : (tag.topicPath || 'No topic set')}
              secondaryTypographyProps={{
                noWrap: true,
                style: { maxWidth: compact ? 200 : 150 },
              }}
            />
            <ListItemSecondaryAction>
              <IconButton
                edge="end"
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleEditTag(tag);
                }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
              <IconButton
                edge="end"
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteTag(tag.id);
                }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </ListItemSecondaryAction>
          </ListItem>
          );
        })
      )}
    </List>
  );

  if (compact) {
    return (
      <Box className="tag-editor-container">
        <Box
          sx={{
            border: '1px solid #BDBDBD',
            borderRadius: 2,
            overflow: 'hidden',
          }}
        >
          <Box sx={{ p: 1.5, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={addNewTag}
            >
              Add Tag
            </Button>
          </Box>
          <Divider />
          {renderTagsList()}
        </Box>

        {/* Edit Tag Dialog */}
        {renderDialog()}
      </Box>
    );
  }

  return (
    <Box className="tag-editor-container">
      {/* Instructions */}
      <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
        Click on the image to add a new tag. Drag existing tags to reposition them.
      </Typography>

      <Box className="tag-editor-layout">
        {/* Media Preview with Tags */}
        <Box className="tag-editor-media-section">
          <div
            ref={mediaContainerRef}
            className="tag-editor-media-container"
            onClick={handleMediaClick}
          >
            {mediaUrl ? (
              <>
                {mediaType === 'video' ? (
                  <video
                    className="tag-editor-media"
                    src={mediaUrl}
                    muted
                    loop
                    autoPlay
                    playsInline
                  />
                ) : (
                  <img
                    className="tag-editor-media"
                    src={mediaUrl}
                    alt="Factory media"
                  />
                )}
                
                {/* Tags Layer */}
                <div className="tag-editor-tags-layer">
                  {tags.map((tag) => (
                    <DataTag
                      key={tag.id}
                      label={tag.label}
                      value={tag.topicPath ? '---' : 'N/A'}
                      position={tag.position}
                      style={tag.style}
                      isEditing={true}
                      onClick={(e) => handleTagClick(e, tag.id)}
                      onDragStart={() => handleTagDragStart(tag.id)}
                      onDragEnd={(e) => handleTagDragEnd(e, tag.id)}
                    />
                  ))}
                </div>
              </>
            ) : (
              <Box className="tag-editor-no-media">
                <Typography color="textSecondary">
                  Upload media first to place tags
                </Typography>
              </Box>
            )}
          </div>
        </Box>

        {/* Tags List Panel */}
        <Paper className="tag-editor-panel" elevation={2}>
          <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="subtitle1" fontWeight="bold">
              Data Tags ({tags.length})
            </Typography>
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={addNewTag}
            >
              Add Tag
            </Button>
          </Box>
          
          <Divider />
          {renderTagsList()}
        </Paper>
      </Box>

      {/* Edit Tag Dialog */}
      {renderDialog()}
    </Box>
  );

  function renderDialog() {
    return (
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        TransitionProps={{ onExited: () => setEditingTag(null) }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editingTag && tags.some(t => t.id === editingTag.id) ? 'Edit Tag' : 'Add New Tag'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Label"
              value={editingTag?.label || ''}
              onChange={(e) => handleEditingTagChange('label', e.target.value)}
              fullWidth
              helperText="Display name shown on the tag"
            />

            {/* Per-node data source override */}
            {groupDataSource && (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Data Source</Typography>
                <ToggleButtonGroup
                  value={editingTag?.dataSource || 'inherit'}
                  exclusive
                  onChange={(e, val) => {
                    if (val !== null) {
                      handleEditingTagChange('dataSource', val === 'inherit' ? '' : val);
                    }
                  }}
                  size="small"
                  fullWidth
                >
                  <ToggleButton value="inherit">
                    Inherit ({groupDataSource === 'live' ? 'Live' : 'Sim'})
                  </ToggleButton>
                  <ToggleButton value="live">Live MQTT</ToggleButton>
                  <ToggleButton value="simulated">Simulated</ToggleButton>
                </ToggleButtonGroup>
              </Box>
            )}

            {/* Simulated: show simulation settings; Live: show MQTT topic */}
            {(editingTag && isTagSimulated(editingTag)) ? (
              <>
                <Divider sx={{ my: 1 }} />
                <Typography variant="subtitle2">Simulation Settings</Typography>

                <FormControl fullWidth size="small">
                  <InputLabel>Value Type</InputLabel>
                  <Select
                    value={editingTag?.simulationConfig?.type || 'numeric'}
                    label="Value Type"
                    onChange={(e) => handleSimConfigChange('type', e.target.value)}
                  >
                    <MenuItem value="numeric">Numeric (random in range)</MenuItem>
                    <MenuItem value="counter">Counter (incrementing)</MenuItem>
                    <MenuItem value="state">State (cycle through values)</MenuItem>
                    <MenuItem value="boolean">Boolean</MenuItem>
                  </Select>
                </FormControl>

                {(editingTag?.simulationConfig?.type === 'numeric' || !editingTag?.simulationConfig?.type) && (
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <TextField
                      label="Min"
                      type="number"
                      value={editingTag?.simulationConfig?.min ?? 0}
                      onChange={(e) => handleSimConfigChange('min', parseFloat(e.target.value) || 0)}
                      sx={{ flex: 1 }}
                      size="small"
                    />
                    <TextField
                      label="Max"
                      type="number"
                      value={editingTag?.simulationConfig?.max ?? 100}
                      onChange={(e) => handleSimConfigChange('max', parseFloat(e.target.value) || 100)}
                      sx={{ flex: 1 }}
                      size="small"
                    />
                  </Box>
                )}

                {editingTag?.simulationConfig?.type === 'counter' && (
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <TextField
                      label="Min Increment"
                      type="number"
                      value={editingTag?.simulationConfig?.min ?? 1}
                      onChange={(e) => handleSimConfigChange('min', parseFloat(e.target.value) || 1)}
                      sx={{ flex: 1 }}
                      size="small"
                      helperText="Minimum increment per step"
                    />
                    <TextField
                      label="Max Increment"
                      type="number"
                      value={editingTag?.simulationConfig?.max ?? 3}
                      onChange={(e) => handleSimConfigChange('max', parseFloat(e.target.value) || 3)}
                      sx={{ flex: 1 }}
                      size="small"
                      helperText="Maximum increment per step"
                    />
                  </Box>
                )}

                {editingTag?.simulationConfig?.type === 'state' && (
                  <TextField
                    label="States"
                    value={statesText}
                    onChange={(e) => setStatesText(e.target.value)}
                    fullWidth
                    size="small"
                    helperText="Comma-separated list of state values (e.g., Running, Idle, Paused)"
                    placeholder="Running, Idle, Paused"
                  />
                )}

                <TextField
                  label="Update Interval (ms)"
                  type="number"
                  value={editingTag?.simulationConfig?.updateIntervalMs ?? 3000}
                  onChange={(e) => handleSimConfigChange('updateIntervalMs', parseInt(e.target.value) || 3000)}
                  fullWidth
                  size="small"
                  helperText="How often the simulated value updates"
                  inputProps={{ min: 500, step: 500 }}
                />
              </>
            ) : (
              <TextField
                label="MQTT Topic Path"
                value={editingTag?.topicPath || ''}
                onChange={(e) => handleEditingTagChange('topicPath', e.target.value)}
                fullWidth
                helperText="Full MQTT topic path (e.g., Tulip/Boston/tulip_experience_center/line1/pressure)"
                placeholder="Tulip/Boston/tulip_experience_center/..."
              />
            )}
            
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Tag Color"
                type="color"
                value={editingTag?.style?.color || '#F28E2B'}
                onChange={(e) => handleEditingTagStyleChange('color', e.target.value)}
                sx={{ width: 120 }}
                InputProps={{
                  sx: { height: 56 }
                }}
              />
              
              <FormControlLabel
                control={
                  <Switch
                    checked={editingTag?.style?.showLabel ?? true}
                    onChange={(e) => handleEditingTagStyleChange('showLabel', e.target.checked)}
                  />
                }
                label="Show Label"
              />
            </Box>

            {!compact && (
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  label="X Position (%)"
                  type="number"
                  value={editingTag?.position?.x || 50}
                  onChange={(e) => handleEditingTagChange('position', {
                    ...editingTag?.position,
                    x: Math.max(0, Math.min(100, parseFloat(e.target.value) || 0)),
                  })}
                  inputProps={{ min: 0, max: 100, step: 1 }}
                  sx={{ flex: 1 }}
                />
                
                <TextField
                  label="Y Position (%)"
                  type="number"
                  value={editingTag?.position?.y || 50}
                  onChange={(e) => handleEditingTagChange('position', {
                    ...editingTag?.position,
                    y: Math.max(0, Math.min(100, parseFloat(e.target.value) || 0)),
                  })}
                  inputProps={{ min: 0, max: 100, step: 1 }}
                  sx={{ flex: 1 }}
                />
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>
            Cancel
          </Button>
          <Button 
            onClick={handleSaveTag} 
            variant="contained"
            disabled={!editingTag?.label || (editingTag && !isTagSimulated(editingTag) && !editingTag?.topicPath)}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    );
  }
};

export default TagEditor;

